import { useMemo, useState } from "react";
import { api } from "../api/client";
import { useData } from "../hooks/useData";
import { I } from "./Icons";
import { Avatar, clsx } from "./Primitives";
import FormDialog, { type FormValues } from "./FormDialog";
import type { Market, Partner } from "../types";

interface Target {
  id: string; name: string; org: string | null; kind: string | null; theme: string | null; market: string | null;
  angle: string | null; owner: string | null; status: Stage; next_step: string | null; next_due: string | null;
  notes: string | null; org_id: string | null; org_name: string | null;
}
type Stage = "identified" | "contacted" | "meeting" | "relationship" | "parked";

const STAGES: { id: Stage; label: string; hint: string }[] = [
  { id: "identified", label: "Identified", hint: "Want to get to know" },
  { id: "contacted", label: "Contacted", hint: "First contact made" },
  { id: "meeting", label: "Meeting held", hint: "We've met" },
  { id: "relationship", label: "Relationship", hint: "Active — an account" },
];
const NEXT: Record<Stage, Stage | null> = { identified: "contacted", contacted: "meeting", meeting: "relationship", relationship: null, parked: "identified" };
const KINDS = ["Operator", "NOC", "Government", "Regulator", "Partner", "Contractor", "Investor", "DFI", "Other"];

const dueLabel = (d: string | null) => {
  if (!d) return null;
  const days = Math.round((new Date(d + "T00:00:00").getTime() - new Date(new Date().toDateString()).getTime()) / 86400000);
  return days < 0 ? `${-days}d overdue` : days === 0 ? "today" : days === 1 ? "tomorrow" : new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
};

export default function RelationshipTargets({ partners, go }: { partners: Record<string, Partner>; go: (screen: string, id?: string) => void }) {
  const { data, refetch } = useData<Target[]>("/api/targets");
  const { data: markets } = useData<Market[]>("/api/markets");
  const [owner, setOwner] = useState("");
  const [theme, setTheme] = useState("");
  const [editing, setEditing] = useState<Target | "new" | null>(null);
  const [showParked, setShowParked] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const all = data || [];
  const themes = useMemo(() => [...new Set(all.map(t => t.theme).filter((x): x is string => !!x))].sort(), [all]);
  const list = all.filter(t => (!owner || t.owner === owner) && (!theme || t.theme === theme));
  const parked = list.filter(t => t.status === "parked");

  const move = async (t: Target, to: Stage) => {
    setErr(null);
    try {
      await api.put(`/api/targets/${t.id}`, { status: to });
      // Reaching "Relationship" makes it a real account in the CRM.
      if (to === "relationship" && !t.org_id) await api.post(`/api/targets/${t.id}/account`, {});
      refetch();
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  };

  const fields = [
    { key: "name", label: "Who", required: true, placeholder: "Company or person" },
    { key: "org", label: "Organisation (if different)" },
    { key: "kind", label: "Type", type: "select" as const, required: true, options: KINDS.map(k => ({ value: k, label: k })) },
    { key: "market", label: "Market", type: "select" as const, options: (markets || []).map(m => ({ value: m.name, label: m.name })) },
    { key: "theme", label: "Campaign / theme", placeholder: "e.g. Seismikk Fishbones, Namibia-møte" },
    { key: "owner", label: "Owner", type: "select" as const, options: Object.values(partners).map(p => ({ value: p.id, label: p.name })) },
    { key: "status", label: "Stage", type: "select" as const, required: true, options: [...STAGES, { id: "parked", label: "Parked" }].map(s => ({ value: s.id, label: s.label })) },
    { key: "angle", label: "Why them / the angle", type: "textarea" as const },
    { key: "next_step", label: "Next step" },
    { key: "next_due", label: "Next step due", type: "date" as const },
    { key: "notes", label: "Notes", type: "textarea" as const },
  ];
  const initial = (t: Target | null): FormValues => ({
    name: t?.name || "", org: t?.org || "", kind: t?.kind || "Operator", market: t?.market || "", theme: t?.theme || "",
    owner: t?.owner || "", status: t?.status || "identified", angle: t?.angle || "", next_step: t?.next_step || "",
    next_due: t?.next_due ? String(t.next_due).slice(0, 10) : "", notes: t?.notes || "",
  });

  const card = (t: Target) => {
    const p = t.owner ? partners[t.owner] : null;
    const due = dueLabel(t.next_due);
    const next = NEXT[t.status];
    return (
      <div key={t.id} className="rt-card" role="button" tabIndex={0} onClick={() => setEditing(t)} onKeyDown={e => e.key === "Enter" && setEditing(t)}>
        <div className="rt-top">
          <span className="rt-name">{t.name}</span>
          <Avatar partner={p || null} size={20} />
        </div>
        <div className="rt-meta">{[t.kind, t.market].filter(Boolean).join(" · ")}</div>
        {t.theme && <span className="rt-theme">{t.theme}</span>}
        {t.angle && <div className="rt-angle">{t.angle}</div>}
        {t.next_step && (
          <div className={clsx("rt-next", due?.includes("overdue") && "late")}>
            {I.arrowRight({ size: 11 })} {t.next_step}{due && <span className="rt-due"> · {due}</span>}
          </div>
        )}
        <div className="rt-foot" onClick={e => e.stopPropagation()}>
          {t.org_id
            ? <button className="ld-link" style={{ marginLeft: 0 }} onClick={() => go("clients", t.org_id!)}>Account: {t.org_name} →</button>
            : <button className="ld-link" style={{ marginLeft: 0 }} onClick={async () => { await api.post(`/api/targets/${t.id}/account`, {}); refetch(); }}>Create account</button>}
          <span style={{ flex: 1 }} />
          {next && <button className="btn ghost sm" title={`Move to ${STAGES.find(s => s.id === next)?.label || next}`} onClick={() => move(t, next)}>
            {t.status === "parked" ? "Revive" : `→ ${STAGES.find(s => s.id === next)?.label}`}
          </button>}
        </div>
      </div>
    );
  };

  return (
    <div className="targets-block">
      <div className="rt-head">
        <div>
          <h2 className="targets-title">Relationship targets</h2>
          <div className="page-sub" style={{ marginTop: 4 }}>
            Organisations we want a relationship with. Move each one along as you make contact — at <em>Relationship</em> it becomes an account.
          </div>
        </div>
        <div className="cd-actions">
          <select className="ld-select" value={owner} onChange={e => setOwner(e.target.value)} aria-label="Owner">
            <option value="">Everyone</option>
            {Object.values(partners).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          {themes.length > 0 && (
            <select className="ld-select" value={theme} onChange={e => setTheme(e.target.value)} aria-label="Campaign">
              <option value="">All campaigns</option>
              {themes.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          )}
          <button className="btn primary" onClick={() => setEditing("new")}>{I.plus({ size: 15 })}Add target</button>
        </div>
      </div>
      {err && <div className="set-notice err" style={{ margin: "0 0 12px" }}>{err}</div>}
      <div className="rt-board">
        {STAGES.map(st => {
          const items = list.filter(t => t.status === st.id);
          return (
            <div key={st.id} className="rt-col">
              <div className="rt-col-head"><span>{st.label}</span><span className="ld-n">{items.length}</span></div>
              <div className="rt-col-hint">{st.hint}</div>
              {items.map(card)}
              {items.length === 0 && <div className="rt-empty">—</div>}
            </div>
          );
        })}
      </div>
      {parked.length > 0 && (
        <div className="rt-parked">
          <button className="r2-toggle" onClick={() => setShowParked(v => !v)}>
            {I.chevron({ size: 13, w: 2, style: { transform: showParked ? "rotate(90deg)" : "none" } })}
            Parked ({parked.length})
          </button>
          {showParked && <div className="rt-parked-list">{parked.map(card)}</div>}
        </div>
      )}

      {editing && (
        <FormDialog
          title={editing === "new" ? "New relationship target" : "Edit target"}
          sub={editing === "new" ? undefined : editing.name}
          fields={fields}
          initial={initial(editing === "new" ? null : editing)}
          saveLabel={editing === "new" ? "Add target" : "Save"}
          onClose={() => setEditing(null)}
          onSave={async v => {
            if (editing === "new") await api.post("/api/targets", v);
            else await api.put(`/api/targets/${editing.id}`, v);
            if (v.status === "relationship" && (editing === "new" || !editing.org_id)) {
              const list2 = await api.get<Target[]>("/api/targets");
              const t = editing === "new" ? list2.find(x => x.name === v.name && !x.org_id) : list2.find(x => x.id === editing.id);
              if (t) await api.post(`/api/targets/${t.id}/account`, {});
            }
            setEditing(null); refetch();
          }}
          onDelete={editing === "new" ? undefined : async () => {
            if (!confirm(`Delete the target ${editing.name}?`)) return;
            await api.delete(`/api/targets/${editing.id}`); setEditing(null); refetch();
          }}
        />
      )}
    </div>
  );
}
